import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  GetAllLicensesAction, 
  AddLicenseAction,
  GetAllActiveLicensesAction,
  UpdateLicenseStatusAction,
  GetLicenseByIdAction
} from "../../actions/LicenseAction";
import { GetAllBusinessesAction } from "../../actions/BusinessAction";
import { GetAllSystemsAction } from "../../actions/SystemAction";

// Import selector functions
import {
  selectAllLicensesData,
  selectAllLicensesLoading,
  selectAllLicensesError,
  selectAddLicenseData,
  selectAddLicenseLoading,
  selectAddLicenseError,
  selectAllActiveLicensesData,
  selectAllActiveLicensesLoading,
  selectAllActiveLicensesError,
  selectUpdateLicenseStatusData,
  selectUpdateLicenseStatusLoading,
  selectUpdateLicenseStatusError,
  selectLicenseByIdData,
  selectLicenseByIdLoading,
  selectLicenseByIdError
} from "../../reducers/LicenseReducer";

import {
  selectAllSystemsData,
  selectAllSystemsLoading,
  selectAllSystemsError
} from "../../reducers/SystemReducer";

import {
  selectAllBusinessesData,
  selectAllBusinessesLoading,
  selectAllBusinessesError
} from "../../reducers/BusinessReducer";

const LicenseManagement = () => {
  const dispatch = useDispatch();

  // Use selector functions instead of direct state paths
  const licensesData = useSelector(selectAllLicensesData);
  const licensesLoading = useSelector(selectAllLicensesLoading);
  const licensesError = useSelector(selectAllLicensesError);

  const addLicenseResult = useSelector(selectAddLicenseData);
  const addLicenseLoading = useSelector(selectAddLicenseLoading);
  const addLicenseError = useSelector(selectAddLicenseError);

  const activeLicensesData = useSelector(selectAllActiveLicensesData);
  const activeLicensesLoading = useSelector(selectAllActiveLicensesLoading);
  const activeLicensesError = useSelector(selectAllActiveLicensesError);

  const updateLicenseStatusResult = useSelector(selectUpdateLicenseStatusData);
  const updateLicenseStatusLoading = useSelector(selectUpdateLicenseStatusLoading);
  const updateLicenseStatusError = useSelector(selectUpdateLicenseStatusError);

  const licenseByIdData = useSelector(selectLicenseByIdData);
  const licenseByIdLoading = useSelector(selectLicenseByIdLoading);
  const licenseByIdError = useSelector(selectLicenseByIdError);

  const businessesData = useSelector(selectAllBusinessesData);
  const systemsData = useSelector(selectAllSystemsData);

  const [licenses, setLicenses] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [systems, setSystems] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [newLicense, setNewLicense] = useState({
    business_id: '',
    system_id: '',
    expiry_date: '',
    subscription_type: 'yearly',
    max_users: 1,
    issue_date: new Date().toISOString().split('T')[0]
  });

  const [selectedStatus, setSelectedStatus] = useState({});
  const [isBusinessDropdownOpen, setIsBusinessDropdownOpen] = useState(false);
  const [isSystemDropdownOpen, setIsSystemDropdownOpen] = useState(false);
  
  const businessDropdownRef = useRef(null);
  const systemDropdownRef = useRef(null);

  // Format licenses data
  const formatAndSetLicenses = (data) => {
    let licensesArray = [];

    if (Array.isArray(data)) {
      licensesArray = data;
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.ResultSet)) licensesArray = data.ResultSet;
      else if (Array.isArray(data.resultSet)) licensesArray = data.resultSet;
      else if (Array.isArray(data.data)) licensesArray = data.data;
      else if (Array.isArray(data.responseBody)) licensesArray = data.responseBody;
      else if (Array.isArray(data.licenses)) licensesArray = data.licenses;
      else if (data.license_id) licensesArray = [data];
    }

    const formatted = licensesArray.map((license, index) => ({
      license_id: license.license_id || license.LicenseID || license.id || `temp-${index}`,
      business_id: license.business_id || license.BusinessID || 'Not specified',
      system_id: license.system_id || license.SystemID || 'Not specified',
      license_key: license.license_key || license.LicenseKey || `KEY-${index}`,
      issue_date: license.issue_date || license.IssueDate || new Date().toISOString().split('T')[0],
      expiry_date: license.expiry_date || license.ExpiryDate || new Date().toISOString().split('T')[0],
      subscription_type: license.subscription_type || license.SubscriptionType || 'yearly',
      max_users: license.max_users || license.MaxUsers || 1,
      status: formatLicenseStatus(license.status || license.Status || "A")
    }));

    setLicenses(formatted);
    // Initialize selected status for each license
    const initialStatus = {};
    formatted.forEach(license => {
      initialStatus[license.license_id] = license.status;
    });
    setSelectedStatus(initialStatus);
  };

  const formatLicenseStatus = (status) => {
    if (status === "A" || status === "Active" || status === "active") {
      return "Active";
    } else if (status === "I" || status === "Inactive" || status === "inactive") {
      return "Inactive";
    } else {
      return "Active";
    }
  };

  const formatAndSetBusinesses = (data) => {
    let businessesArray = [];

    if (Array.isArray(data)) {
      businessesArray = data;
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.ResultSet)) businessesArray = data.ResultSet;
      else if (Array.isArray(data.resultSet)) businessesArray = data.resultSet;
      else if (Array.isArray(data.data)) businessesArray = data.data;
      else if (Array.isArray(data.responseBody)) businessesArray = data.responseBody;
      else if (Array.isArray(data.businesses)) businessesArray = data.businesses;
      else if (data.business_id) businessesArray = [data];
    }

    const formatted = businessesArray.map((business, index) => ({
      business_id: business.business_id || business.BusinessID || business.id || `temp-${index}`,
      business_name: business.business_name || business.BusinessName || business.name || 'Unnamed'
    }));

    setBusinesses(formatted);
  };

  const formatAndSetSystems = (data) => {
    let systemsArray = [];

    if (Array.isArray(data)) {
      systemsArray = data;
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.ResultSet)) systemsArray = data.ResultSet;
      else if (Array.isArray(data.resultSet)) systemsArray = data.resultSet;
      else if (Array.isArray(data.data)) systemsArray = data.data;
      else if (Array.isArray(data.responseBody)) systemsArray = data.responseBody;
      else if (Array.isArray(data.systems)) systemsArray = data.systems;
      else if (data.system_id) systemsArray = [data];
    }

    const formatted = systemsArray.map((system, index) => ({
      system_id: system.system_id || system.SystemID || system.id || `temp-${index}`,
      system_name: system.system_name || system.SystemName || system.name || 'Unnamed'
    }));

    setSystems(formatted);
  };

  // Load all licenses
  const loadLicenses = async () => {
    try {
      if (viewMode === 'active') {
        await dispatch(GetAllActiveLicensesAction());
      } else {
        await dispatch(GetAllLicensesAction());
      }
    } catch (error) {
      console.error('Error loading licenses:', error);
    }
  };

  // Load businesses
  const loadBusinesses = async () => {
    try {
      await dispatch(GetAllBusinessesAction());
    } catch (error) {
      console.error('Error loading businesses:', error);
    }
  };

  // Load systems
  const loadSystems = async () => {
    try {
      await dispatch(GetAllSystemsAction());
    } catch (error) {
      console.error('Error loading systems:', error);
    }
  };

  // Initial load
  useEffect(() => {
    loadLicenses();
    loadBusinesses();
    loadSystems();
    setCurrentPage(1); // Reset to first page when view mode changes
  }, [viewMode]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (businessDropdownRef.current && !businessDropdownRef.current.contains(event.target)) {
        setIsBusinessDropdownOpen(false);
      }
      if (systemDropdownRef.current && !systemDropdownRef.current.contains(event.target)) {
        setIsSystemDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update licenses when data changes
  useEffect(() => {
    if (licensesData) {
      formatAndSetLicenses(licensesData);
    }
  }, [licensesData]);

  useEffect(() => {
    if (activeLicensesData) {
      formatAndSetLicenses(activeLicensesData);
    }
  }, [activeLicensesData]);

  // Handle search by ID response
  useEffect(() => {
    if (licenseByIdData && !licenseByIdLoading) {
      formatAndSetLicenses(licenseByIdData);
      setCurrentPage(1);
    }
  }, [licenseByIdData, licenseByIdLoading]);

  useEffect(() => {
    if (businessesData) {
      formatAndSetBusinesses(businessesData);
    }
  }, [businessesData]);

  useEffect(() => {
    if (systemsData) {
      formatAndSetSystems(systemsData);
    }
  }, [systemsData]);

  // Handle add license success
  useEffect(() => {
    if (addLicenseResult && !addLicenseLoading) {
      console.log('New license added successfully');
      loadLicenses();
      setShowAddForm(false);
      setNewLicense({
        business_id: '',
        system_id: '',
        expiry_date: '',
        subscription_type: 'yearly',
        max_users: 1,
        issue_date: new Date().toISOString().split('T')[0]
      });
      setCurrentPage(1);
    }
  }, [addLicenseResult, addLicenseLoading]);

  // Handle update status success
  useEffect(() => {
    if (updateLicenseStatusResult && !updateLicenseStatusLoading) {
      console.log('License status updated successfully');
      loadLicenses();
    }
  }, [updateLicenseStatusResult, updateLicenseStatusLoading]);

  // Search handler
  const handleSearch = async (e) => {
    e.preventDefault();

    if (!searchTerm.trim()) {
      // If search is empty, load all licenses
      await loadLicenses();
      return;
    }

    try {
      // Search by license ID
      await dispatch(GetLicenseByIdAction(searchTerm));
    } catch (error) {
      console.error('Error searching licenses:', error);
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    loadLicenses();
  };

  // Add license handler
  const handleAddLicense = async (e) => {
    e.preventDefault();

    if (!newLicense.business_id || !newLicense.system_id || !newLicense.expiry_date) {
      alert('Business, System and Expiry Date are required');
      return;
    }

    try {
      await dispatch(AddLicenseAction(newLicense));
    } catch (error) {
      console.error('Error adding license:', error);
    }
  };

  // Input change handler
  const handleLicenseInputChange = (e) => {
    setNewLicense(prev => ({ 
      ...prev, 
      [e.target.name]: e.target.value 
    }));
  };

  // Status update handler
  const getApiStatusCode = (displayStatus) => {
    switch (displayStatus) {
      case "Active": return "A";
      case "Inactive": return "I";
      default: return "A";
    }
  };

  const handleStatusUpdate = async (licenseId, newDisplayStatus) => {
    if (!licenseId || !newDisplayStatus) {
      alert('License ID and Status are required');
      return;
    }

    try {
      const apiStatus = getApiStatusCode(newDisplayStatus);
      await dispatch(UpdateLicenseStatusAction(licenseId, apiStatus));
    } catch (error) {
      console.error('Error updating license status:', error);
    }
  };

  const handleStatusChange = (licenseId, newStatus) => {
    setSelectedStatus(prev => ({
      ...prev,
      [licenseId]: newStatus
    }));
    handleStatusUpdate(licenseId, newStatus);
  };

  // Close add form
  const handleCloseAddForm = () => {
    setShowAddForm(false);
    setNewLicense({
      business_id: '',
      system_id: '',
      expiry_date: '',
      subscription_type: 'yearly',
      max_users: 1,
      issue_date: new Date().toISOString().split('T')[0]
    });
    setIsBusinessDropdownOpen(false);
    setIsSystemDropdownOpen(false);
  };

  // Get business name by ID
  const getBusinessName = (businessId) => {
    const business = businesses.find(b => b.business_id === businessId);
    return business ? business.business_name : 'Unknown';
  };

  // Get business display name with ID for table
  const getBusinessDisplayName = (businessId) => {
    const business = businesses.find(b => b.business_id === businessId);
    if (!business) return 'Unknown';
    return `${business.business_name} (ID: ${business.business_id})`;
  };

  // Get system name by ID
  const getSystemName = (systemId) => {
    const system = systems.find(s => s.system_id === systemId);
    return system ? system.system_name : 'Unknown';
  };

  // Get unique businesses for dropdown (avoid duplicates with same name)
  const getUniqueBusinesses = () => {
    const uniqueMap = new Map();
    businesses.forEach(business => {
      const key = `${business.business_name}-${business.business_id}`;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, business);
      }
    });
    return Array.from(uniqueMap.values());
  };

  // Handle business selection
  const handleBusinessSelect = (businessId) => {
    setNewLicense(prev => ({ ...prev, business_id: businessId }));
    setIsBusinessDropdownOpen(false);
  };

  // Handle system selection
  const handleSystemSelect = (systemId) => {
    setNewLicense(prev => ({ ...prev, system_id: systemId }));
    setIsSystemDropdownOpen(false);
  };

  // Get selected business name
  const getSelectedBusinessName = () => {
    if (!newLicense.business_id) return 'Select Business';
    const business = businesses.find(b => b.business_id === newLicense.business_id);
    return business ? `${business.business_name} (ID: ${business.business_id})` : 'Select Business';
  };

  // Get selected system name
  const getSelectedSystemName = () => {
    if (!newLicense.system_id) return 'Select System';
    const system = systems.find(s => s.system_id === newLicense.system_id);
    return system ? system.system_name : 'Select System';
  };

  // Status badge colors
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Subscription badge colors
  const getSubscriptionBadgeColor = (type) => {
    switch (type) {
      case 'yearly':
        return 'bg-purple-100 text-purple-800';
      case 'monthly':
        return 'bg-blue-100 text-blue-800';
      case 'lifetime':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLicenses = licenses.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(licenses.length / itemsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">License Management</h1>
        <p className="text-sm md:text-base text-gray-600">Manage all licenses and their status</p>
      </div>

      {/* Error Messages */}
      {(licensesError || addLicenseError || activeLicensesError || updateLicenseStatusError || licenseByIdError) && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm md:text-base">
          <strong>Error:</strong> {licensesError || addLicenseError || activeLicensesError || updateLicenseStatusError || licenseByIdError}
        </div>
      )}

      {/* Search and Controls */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <div className="flex-1 max-w-full lg:max-w-md relative w-full">
          <input
            type="text"
            placeholder="Search licenses by ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={licensesLoading || activeLicensesLoading || licenseByIdLoading}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              disabled={licensesLoading || activeLicensesLoading || licenseByIdLoading}
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* View Mode and Add Button */}
        <div className="flex items-center space-x-2 md:space-x-4 w-full lg:w-auto">
          <div className="flex items-center space-x-2">
            <Filter size={18} className="text-gray-400 hidden md:block" />
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm w-full md:w-auto"
              disabled={licensesLoading || activeLicensesLoading || licenseByIdLoading}
            >
              <option value="all">All Licenses</option>
              <option value="active">Active Only</option>
            </select>
          </div>
          
          <button 
            onClick={() => setShowAddForm(true)}
            disabled={addLicenseLoading || licensesLoading || activeLicensesLoading || licenseByIdLoading}
            className="bg-blue-500 text-white px-4 md:px-6 py-2 rounded-lg flex items-center justify-center space-x-2 hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed w-full md:w-auto"
          >
            <Plus size={18} />
            <span className="text-sm md:text-base">{addLicenseLoading ? 'Adding...' : 'Add New License'}</span>
          </button>
        </div>
      </div>

      {/* Add License Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 md:p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl md:max-w-3xl lg:max-w-4xl max-h-[85vh] md:max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 md:p-6 border-b">
              <h2 className="text-lg md:text-xl font-bold text-gray-800">Add New License</h2>
              <button 
                onClick={handleCloseAddForm}
                disabled={addLicenseLoading}
                className="disabled:opacity-50 text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddLicense} className="p-4 md:p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Custom Business Dropdown */}
                <div ref={businessDropdownRef} className="col-span-1 md:col-span-2 lg:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business *</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsBusinessDropdownOpen(!isBusinessDropdownOpen)}
                      disabled={addLicenseLoading}
                      className={`w-full px-3 py-2 border rounded-lg text-left flex justify-between items-center disabled:bg-gray-100 ${!newLicense.business_id ? 'text-gray-500' : 'text-gray-800'
                        } ${isBusinessDropdownOpen ? 'ring-2 ring-blue-500 border-blue-500' : 'border-gray-300'}`}
                    >
                      <span className="truncate text-sm">{getSelectedBusinessName()}</span>
                      {isBusinessDropdownOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                    
                    {isBusinessDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 md:max-h-60 overflow-y-auto">
                        {/* Business List Header */}
                        <div className="sticky top-0 bg-gray-50 p-2 md:p-3 border-b">
                          <div className="font-medium text-xs md:text-sm text-gray-700">
                            Select a Business ({getUniqueBusinesses().length} available)
                          </div>
                        </div>
                        
                        {/* Business List */}
                        <div className="py-1">
                          {getUniqueBusinesses().length > 0 ? (
                            getUniqueBusinesses().map(business => (
                              <button
                                key={`${business.business_id}-${business.business_name}`}
                                type="button"
                                onClick={() => handleBusinessSelect(business.business_id)}
                                className={`w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors ${newLicense.business_id === business.business_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                              >
                                <div className="font-medium text-sm">{business.business_name}</div>
                                <div className="text-xs text-gray-500">ID: {business.business_id}</div>
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-gray-500 text-center text-sm">
                              No businesses available
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Custom System Dropdown */}
                <div ref={systemDropdownRef} className="col-span-1 md:col-span-2 lg:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">System *</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsSystemDropdownOpen(!isSystemDropdownOpen)}
                      disabled={addLicenseLoading}
                      className={`w-full px-3 py-2 border rounded-lg text-left flex justify-between items-center disabled:bg-gray-100 ${!newLicense.system_id ? 'text-gray-500' : 'text-gray-800'
                        } ${isSystemDropdownOpen ? 'ring-2 ring-blue-500 border-blue-500' : 'border-gray-300'}`}
                    >
                      <span className="truncate text-sm">{getSelectedSystemName()}</span>
                      {isSystemDropdownOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                    
                    {isSystemDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 md:max-h-60 overflow-y-auto">
                        {/* System List Header */}
                        <div className="sticky top-0 bg-gray-50 p-2 md:p-3 border-b">
                          <div className="font-medium text-xs md:text-sm text-gray-700">
                            Select a System ({systems.length} available)
                          </div>
                        </div>
                        
                        {/* System List */}
                        <div className="py-1">
                          {systems.length > 0 ? (
                            systems.map(system => (
                              <button
                                key={system.system_id}
                                type="button"
                                onClick={() => handleSystemSelect(system.system_id)}
                                className={`w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors ${newLicense.system_id === system.system_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                              >
                                <div className="font-medium text-sm">{system.system_name}</div>
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-gray-500 text-center text-sm">
                              No systems available
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subscription Type</label>
                  <select
                    name="subscription_type"
                    value={newLicense.subscription_type}
                    onChange={handleLicenseInputChange}
                    disabled={addLicenseLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 text-sm"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="lifetime">Lifetime</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Users</label>
                  <input
                    type="number"
                    name="max_users"
                    value={newLicense.max_users}
                    onChange={handleLicenseInputChange}
                    disabled={addLicenseLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 text-sm"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date *</label>
                  <div className="relative">
                    <input
                      type="date"
                      name="expiry_date"
                      value={newLicense.expiry_date}
                      onChange={handleLicenseInputChange}
                      required
                      disabled={addLicenseLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Issue Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      name="issue_date"
                      value={newLicense.issue_date}
                      onChange={handleLicenseInputChange}
                      disabled={addLicenseLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 text-sm"
                    />
                  </div>
                </div>
              </div>

              {addLicenseError && (
                <div className="p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                  {addLicenseError}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseAddForm}
                  disabled={addLicenseLoading}
                  className="px-3 md:px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addLicenseLoading || !newLicense.business_id || !newLicense.system_id || !newLicense.expiry_date}
                  className="px-3 md:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed font-medium text-sm"
                >
                  {addLicenseLoading ? 'Adding...' : 'Add License'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Loading State */}
      {(licensesLoading || activeLicensesLoading || licenseByIdLoading) && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-600 text-sm md:text-base">
            {licenseByIdLoading ? 'Searching licenses...' : 
             viewMode === 'active' ? 'Loading active licenses...' : 'Loading all licenses...'}
          </p>
        </div>
      )}

      {/* Licenses Table */}
      {!licensesLoading && !activeLicensesLoading && !licenseByIdLoading && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[1024px] md:min-w-0">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">License ID</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">System</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">License Key</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issue Date</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max Users</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentLicenses.length > 0 ? (
                    currentLicenses.map((license, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap text-sm font-medium text-gray-900">{license.license_id}</td>
                        <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap text-sm text-gray-900">{getBusinessDisplayName(license.business_id)}</td>
                        <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap text-sm text-gray-900">{getSystemName(license.system_id)}</td>
                        <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap text-sm text-gray-900 font-mono">{license.license_key}</td>
                        <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap text-sm text-gray-900">{license.issue_date}</td>
                        <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap text-sm text-gray-900">{license.expiry_date}</td>
                        <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSubscriptionBadgeColor(license.subscription_type)}`}>
                            {license.subscription_type}
                          </span>
                        </td>
                        <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap text-sm text-gray-900">{license.max_users}</td>
                        <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <select
                              value={selectedStatus[license.license_id] || license.status}
                              onChange={(e) => handleStatusChange(license.license_id, e.target.value)}
                              disabled={updateLicenseStatusLoading}
                              className={`px-2 md:px-3 py-1 text-xs font-semibold rounded border ${getStatusBadgeColor(selectedStatus[license.license_id] || license.status)} focus:outline-none focus:ring-1 focus:ring-blue-500`}
                            >
                              <option value="Active">Active</option>
                              <option value="Inactive">Inactive</option>
                            </select>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="px-6 py-8 text-center text-gray-500 text-sm md:text-base">
                        {searchTerm ? 'No licenses match your search' : 'No licenses found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Pagination */}
          {licenses.length > itemsPerPage && (
            <div className="flex flex-col md:flex-row items-center justify-between px-4 md:px-6 py-4 bg-gray-50 border-t border-gray-200 gap-4 md:gap-0">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
                <span className="font-medium">
                  {indexOfLastItem > licenses.length ? licenses.length : indexOfLastItem}
                </span>{" "}
                of <span className="font-medium">{licenses.length}</span> licenses
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex space-x-1">
                  {getPageNumbers().map((pageNumber, index) => (
                    <React.Fragment key={index}>
                      {pageNumber === '...' ? (
                        <span className="px-3 py-1 text-gray-500">...</span>
                      ) : (
                        <button
                          onClick={() => handlePageClick(pageNumber)}
                          className={`px-3 py-1 rounded text-sm font-medium ${currentPage === pageNumber
                              ? 'bg-blue-500 text-white'
                              : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                          {pageNumber}
                        </button>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LicenseManagement;